import {
  findPlanShapeViolation,
  findTariffViolation,
  PRICING_PLAN_TIERS,
  PricingPlansSchema,
  PricingRuleSetSchema,
  TierRulesSchema,
  resolveAuctionFeeChf,
  resolvePlanSuccessFee,
  type PricingPlan,
  type PricingPlans,
  type PricingPlanTier,
  type PricingRuleSet,
} from '@carsfinders/contracts';

import { apiFetch } from '../../lib/api';

/**
 * Admin tariffs (Phase 7 — SC-8). The owner edits the commission grid HERE
 * rather than through a deploy: `PUT /api/admin/pricing/rules` has existed since
 * Phase 8 but had no screen, so the only way to change a tariff was to edit the
 * seed and ship it.
 *
 * Everything below is pure so the page's preview and its save-time guard can be
 * tested without a DOM — `pricing-preview.test.ts` is that test.
 */

/**
 * The five prices the grid is reviewed on (volume-tier commission, SC-1).
 *
 * THEY ARE EUR (Phase 7 review, I2). `resolvePlanSuccessFee` takes the
 * EUR-converted hammer price, so the ceiling, this preview and the client-facing
 * share are all measured on one basis. Every column header and every cell in the
 * UI must SAY so: reading an unlabelled "1500" as a CHF lot is exactly how the
 * owner came to sign off on 8.0% while the bid screen appeared to show 8.4%.
 *
 * €500 and €2,000 sit under every tier's minimum; at €5,260 retail's 6% and
 * dealer's 4.5% have just cleared their floors while Dealer Pro's 3% has not;
 * €10,000 and €20,000 are the percentage alone.
 */
export const CONTROL_PRICES_EUR = [500, 2000, 5260, 10000, 20000] as const;

/**
 * The CHF hammer prices the Swiss-leg (platform fee) preview runs on. Separate
 * from {@link CONTROL_PRICES_EUR}: that set moved to the SC-1 prices, while this
 * one still ends at `PLATFORM_FEE_PROBE_CHF` (CHF 40,000) — the price the API
 * probes for a platform fee that is zero everywhere.
 */
export const CONTROL_PRICES_CHF = [1500, 3500, 8000, 15000, 25000, 40000] as const;

export interface PreviewCell {
  /** The EUR lot price the fee is measured on — named next to the fee on screen. */
  price: number;
  fee: number;
  /** fee / price — shown next to the fee; the base is the EUR lot price. */
  share: number;
  /**
   * The tier minimum decided the fee («мін.»): the percentage alone would have
   * charged less. Never true for a banded tier — bands ignore the minimum.
   */
  atMinimum: boolean;
  /**
   * The cell's tier is the one `findPlanShapeViolation` refuses (flat part,
   * percentage above 8%, minimum above EUR 300, monthly fee) — painted red so the
   * preview agrees with the server. A high SHARE alone is not a violation: a
   * cheap lot paying the tier minimum is accepted knowingly.
   */
  tierRefused: boolean;
}

export interface PreviewRow {
  tier: PricingPlanTier;
  cells: PreviewCell[];
}

/** Whether the minimum, not the percentage, set the fee for `price` under `plan`. */
function minimumBinds(plan: PricingPlan, price: number): boolean {
  if (plan.marginalBands && plan.marginalBands.length > 0) return false;
  return plan.successFeeFlat + plan.successFeePct * Math.max(0, price) < plan.minFee;
}

/**
 * "Commission for a lot of €X" across the control prices, one row per tier: the
 * fee, its share of the EUR price, and whether the tier minimum bound.
 *
 * The share is what the owner actually reviews: a fee of EUR 900 says nothing
 * on its own, and it was precisely the share (EUR 975 on a CHF 1,500 lot — 65%)
 * that nobody was looking at when the old grid shipped.
 */
export function buildPreview(
  plans: PricingPlans,
  prices: readonly number[] = CONTROL_PRICES_EUR,
): PreviewRow[] {
  const refusedTier = findPlanShapeViolation(plans)?.tier ?? null;
  return PRICING_PLAN_TIERS.map((tier) => ({
    tier,
    cells: prices.map((price) => {
      const plan = plans[tier];
      const fee = resolvePlanSuccessFee(plan, price);
      return {
        price,
        fee,
        share: price > 0 ? fee / price : 0,
        atMinimum: minimumBinds(plan, price),
        tierRefused: tier === refusedTier,
      };
    }),
  }));
}

/** One control price through the Swiss leg — what the platform fee edit does. */
export interface PlatformFeeCell {
  /** Hammer price, CHF. */
  priceChf: number;
  /** The platform fee that hammer price attracts, CHF. */
  feeChf: number;
  /** The same fee in EUR, at the rule set's own `chfToEurRate`. */
  feeEur: number;
  /** feeChf / priceChf — how the Swiss leg scales. */
  share: number;
}

/**
 * Live preview for the fields BESIDE the grid: the platform fee, its floor and
 * the FX rate.
 *
 * Added because the page could not edit them at all (Phase 7 review, C3b) and a
 * field the owner can edit without seeing its effect is how `ua-2026-01`'s flat
 * CHF 350 survived unnoticed. The control prices here are CHF — the platform fee
 * is a CHF fee on the CHF hammer price — and the UI labels them CHF, next to a
 * EUR table that is labelled EUR.
 */
export function buildPlatformFeePreview(
  rules: PricingRuleSet,
  pricesChf: readonly number[] = CONTROL_PRICES_CHF,
): PlatformFeeCell[] {
  return pricesChf.map((priceChf) => {
    const feeChf = resolveAuctionFeeChf(rules.swissExport, priceChf);
    return {
      priceChf,
      feeChf,
      feeEur: feeChf * rules.swissExport.chfToEurRate,
      share: priceChf > 0 ? feeChf / priceChf : 0,
    };
  });
}

/**
 * Whether this rule set may be published at all.
 *
 * Deliberately the SAME `findTariffViolation` the API refuses with — a
 * client-side check that validated less would let the owner press Save on a
 * document the server then rejects, and one that validated more would block a
 * document the server would have accepted. It covers every field this page can
 * now edit, which is the point: a page that can edit `pensionFundTiers` but only
 * validates `plans` is how an unpublishable document gets composed.
 *
 * Behind it, the SHAPE the API parses the body with before it ever calls
 * `findTariffViolation` (`PricingPlansSchema`, `TierRulesSchema` — its 400). Not
 * a second rule: a cleared number box is `NaN` / `0`, which the commercial check
 * cannot see and the server's parse refuses, and the owner should hear that here.
 */
export function validateRules(rules: PricingRuleSet): {
  ok: boolean;
  message: string | null;
} {
  const violation = findTariffViolation(rules);
  if (violation) return { ok: false, message: violation.message };
  const shapeIssue = [
    { root: 'plans', result: PricingPlansSchema.safeParse(rules.plans) },
    { root: 'tierRules', result: TierRulesSchema.nullable().safeParse(rules.tierRules) },
  ]
    .map(({ root, result }) => ({ root, issue: result.error?.issues[0] }))
    .find(({ issue }) => issue !== undefined);
  if (!shapeIssue?.issue) return { ok: true, message: null };
  const path = [shapeIssue.root, ...shapeIssue.issue.path].join('.');
  return { ok: false, message: `${path}: ${shapeIssue.issue.message}` };
}

/**
 * The rule set currently in force, for the page to load and edit.
 *
 * PARSED, not cast. `apiFetch<PricingRuleSet>` is a bare cast over
 * `response.json()`, and JSON has no Date: `customs.validFrom` arrives as the
 * string `"2026-01-01T00:00:00.000Z"` while the type claims a `Date`. The page
 * then called `.getTime()` on it the moment the operator edited the version
 * (`pricing-edits.ts findPublishBlocker`) and blanked the screen with a
 * TypeError — on the one screen the whole phase's migration story depends on.
 * Typecheck could not see it because the cast lied; the unit tests could not see
 * it because they build their fixture with `PricingRuleSetSchema.parse`, which
 * is exactly what was missing here.
 */
export function fetchCurrentRules(): Promise<PricingRuleSet> {
  return apiFetch<unknown>('pricing/rules/current').then((payload) =>
    PricingRuleSetSchema.parse(payload),
  );
}

/**
 * Publish a NEW immutable version. The caller must have bumped
 * `customs.version` — re-using one answers 409, which is the immutability
 * invariant and not an error to paper over.
 */
export function publishRules(rules: PricingRuleSet): Promise<{ success: boolean }> {
  return apiFetch('admin/pricing/rules', { method: 'PUT', body: rules });
}
